import config from '../../../config/config';
import DefaultApiLimit from '../../../config/defaultLimit';
import { StationStatusChangeParameters } from '../../../interface/charging-location.interface';
import deleteRequest from '../base/delete';
import getRequest from '../base/get';
import patchRequest from '../base/patch';
import patchStream from '../base/patchStream';
import postStream from '../base/postStream';

interface QueryParameters {
  limit?: number;
  [key: string]: any;
}
export default {
  /**
   * Add station API
   * @param {any} data - The station data
   * @param {any} handleUploadProgress - Callback for tracking upload progress
   * @returns {any} A promise that resolves with the response
   */
  addStation: (data: any, handleUploadProgress: (progress: any) => void): Promise<any> => {
    return postStream(
      `${config.commonApiUrl}api/common/v1/charging-station`,
      data,
      true,
      handleUploadProgress,
    );
  },
  /**
   * Fetch stations API
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the stations
   */
  fetchStations: (query: QueryParameters = {}) => {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(`${config.commonApiUrl}api/common/v1/charging-station`, {
      ...parameters,
    });
  },
  /**
   * Fetch stations API for export
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the exported data
   */
  fetchStationsExport: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(`${config.commonApiUrl}api/common/v1/charging-station/export-data`, {
      ...parameters,
    });
  },
  /**
   * Fetch all stations API
   * @returns {any} A promise that resolves with the station names
   */
  fetchAllStations: () => {
    return getRequest(`${config.commonApiUrl}api/common/v1/charging-station/station-names`, {});
  },
  /**
   * Fetch all stations details API
   * @returns {any} A promise that resolves with the station details
   */
  fetchAllStationDetails: () => {
    return getRequest(
      `${config.commonApiUrl}api/common/v1/charging-station/station-all-details`,
      {},
    );
  },
  /**
   * Edit station API
   * @param {any} id - The station ID
   * @param {any} data - The station data
   * @param {any} handleUploadProgress - Callback for tracking upload progress
   * @returns {any} A promise that resolves with the response
   */
  editStation: (
    id: string,
    data: any,
    handleUploadProgress: (progress: any) => void,
  ): Promise<any> => {
    return patchStream(
      `${config.commonApiUrl}api/common/v1/charging-station/${id}`,
      data,
      true,
      handleUploadProgress,
    );
  },
  /**
   * Edit station status API
   * @param {any} id - The station ID
   * @param {any} data - The status data
   * @returns {any} A promise that resolves with the response
   */
  editAvailableStatusStation: (id: string, data: any): Promise<any> => {
    return patchRequest(
      `${config.commonApiUrl}api/common/v1/charging-station/status/${id}`,
      data,
      false,
    );
  },
  /**
   * Delete Station API
   * @param {any} id - The station ID to delete
   * @returns {any} A promise that resolves with the response
   */
  deleteStation: (id: string): Promise<any> => {
    return deleteRequest(`${config.commonApiUrl}api/common/v1/charging-station/${id}`);
  },

  /**
   * Fetch approved station API
   * @returns {any} A promise that resolves with the list filters
   */
  fetchListFilters: () => {
    return getRequest(`${config.commonApiUrl}api/common/v1/charging-station/list-filters`);
  },
  /**
   * Fetch the charging station list with pagination
   * @param {any} id - The id
   */
  fetchChargingStationByStationId: (id: string): Promise<any> => {
    return getRequest(`${config.commonApiUrl}api/common/v1/charging-station/${id}`);
  },
  /**
   * Fetch the charging station list with pagination
   * @param {any} id - The id
   */
  fetchChargingPointByStationId: (id: string): Promise<any> => {
    return getRequest(
      `${config.commonApiUrl}api/common/v1/charging-point/station/device-details/${id}`,
    );
  },
  /**
   * Fetch the common details
   * @param {any} commonId - common id
   * @returns {any} - common details
   */
  fetchFacilityDetails: (commonId: string): Promise<any> => {
    return getRequest(`${config.commonApiUrl}api/common/v1/common-tag/${commonId}`);
  },
  /**
   * pending notification count
   * @returns {any} A promise that resolves with the count
   */
  fetchPendingNotificationCount: (): Promise<any> => {
    return getRequest(`${config.commonApiUrl}api/common/v1/charging-station/pending-count`);
  },
  /**
   * Delete the station api call function
   * @param {any} id - The station ID
   * @param {any} data - The status change parameters
   * @returns {any} A promise that resolves with the response
   */
  statusChangeChargingStation: (id: string, data: StationStatusChangeParameters): Promise<any> => {
    const parameters = { ...data };
    return patchRequest(
      `${config.commonApiUrl}api/common/v1/charging-station/submission-status/${id}`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch station status counts (dummy API)
   * @returns {any} A promise that resolves with dummy status count data
   */
  fetchStationStatusCount: (): Promise<any> => {
    return Promise.resolve({
      data: {
        totalStationsCount: 15,
        approvedCount: 10,
        pendingCount: 3,
        rejectedCount: 2,
        totalDeviceCount: 8,
        totalOnlineDeviceCount: 6,
        totalOfflineDeviceCount: 2,
      },
    });
  },
};
